import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DynamicApiService } from '../dynamic/dynamic-api.service';
import { PublicFormAnalyticsEventItem } from '../dynamic/dynamic.models';

const FLUSH_DEBOUNCE_MS = 600;
const FIELD_DEBOUNCE_MS = 800;

@Injectable()
export class FormAnalyticsClient {
  private readonly api = inject(DynamicApiService);

  private slug = '';
  private sessionId = '';
  private submitted = false;
  private dropoffSent = false;
  private readonly queue: PublicFormAnalyticsEventItem[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly fieldTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly lastFieldEventAt = new Map<string, number>();

  bind(slug: string): void {
    this.slug = slug;
    this.sessionId = this.createSessionId();
    this.submitted = false;
    this.dropoffSent = false;
    this.queue.length = 0;
    this.fieldTimers.forEach(timer => clearTimeout(timer));
    this.fieldTimers.clear();
    this.lastFieldEventAt.clear();
  }

  markSubmitted(): void {
    this.submitted = true;
    this.flush(true);
  }

  trackStart(step?: string | null): void {
    this.enqueue({
      eventType: 'start',
      occurredAtUtc: new Date().toISOString(),
      step: step ?? null
    });
    this.scheduleFlush();
  }

  trackFieldInteraction(fieldKey: string): void {
    this.scheduleFieldEvent(fieldKey, 'field_interaction');
  }

  trackFieldError(fieldKey: string): void {
    this.scheduleFieldEvent(fieldKey, 'field_error');
  }

  trackDropoff(step?: string | null): void {
    if (this.submitted || this.dropoffSent || !this.slug) {
      return;
    }
    this.dropoffSent = true;
    this.enqueue({
      eventType: 'dropoff',
      occurredAtUtc: new Date().toISOString(),
      step: step ?? null
    });
    this.flush(true);
  }

  dispose(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.fieldTimers.forEach(timer => clearTimeout(timer));
    this.fieldTimers.clear();
  }

  private scheduleFieldEvent(fieldKey: string, eventType: 'field_interaction' | 'field_error'): void {
    const existing = this.fieldTimers.get(fieldKey);
    if (existing) {
      clearTimeout(existing);
    }

    const timer = setTimeout(() => {
      this.fieldTimers.delete(fieldKey);
      const now = Date.now();
      const last = this.lastFieldEventAt.get(fieldKey) ?? 0;
      if (now - last < FIELD_DEBOUNCE_MS) {
        return;
      }
      this.lastFieldEventAt.set(fieldKey, now);
      this.enqueue({
        eventType,
        fieldKey,
        occurredAtUtc: new Date().toISOString()
      });
      this.scheduleFlush();
    }, 120);

    this.fieldTimers.set(fieldKey, timer);
  }

  private enqueue(event: PublicFormAnalyticsEventItem): void {
    this.queue.push(event);
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush(false);
    }, FLUSH_DEBOUNCE_MS);
  }

  private flush(force: boolean): void {
    if (!this.slug || !this.sessionId || !this.queue.length) {
      return;
    }

    const events = this.queue.splice(0, this.queue.length);
    const payload = JSON.stringify({ sessionId: this.sessionId, events });
    const url = this.buildEndpointUrl();

    if (force && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) {
          return;
        }
      } catch {
        // Fall through to HttpClient.
      }
    }

    this.api.postPublicAnalyticsEvents(this.slug, { sessionId: this.sessionId, events }).subscribe({
      error: () => {
        // Best-effort analytics; swallow errors.
      }
    });
  }

  private buildEndpointUrl(): string {
    return `${environment.apiBaseUrl}/api/v1/dynamic-forms/public/${encodeURIComponent(this.slug)}/analytics-events`;
  }

  private createSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}
